// Joker-stop create via the Service Layer
// (FR-FE-JOKER-AS-VISIT-FLOW-2026-06-10).
//
// A "joker" is NOT a separate justify-form artifact in its own table — it is
// an ad-hoc stop the rep adds to the live mission and then walks straight into
// the normal Besuch (visit) flow. The Service Layer already owns this:
//
//   POST /v1/missions/{mission_id}/joker  (SL create_joker_plural)
//
// inserts a REAL `mission_lead` stop (stop_type='joker') against the mission
// and returns the created stop in `data.stop`. There is NO `joker_stops`
// table write here, NO direct-Supabase insert, NO new endpoint — the FE just
// calls the existing SL route and routes the rep onto `stop.id`.
//
// FAIL-LOUD (D-33): a 4xx/5xx ServiceHttpError propagates (wrapped with a
// domain prefix), and a 2xx whose envelope carries no stop id is itself an
// error — we never pretend a stop was created. The caller surfaces the typed
// error; there is no direct-Supabase fallback.

import { serviceFetch } from '../http/serviceLayerClient'
import { buildCtx, withWrappedError } from './missionVisits.helpers'

/** The mission_lead stop the SL returns from create_joker_plural. */
export interface JokerStopCreated {
  /** mission_lead row id — the rep navigates into /scout/visit-report/{id}. */
  id: string
  mission_id?: string
  lead_id?: string | null
  company_name?: string | null
  stop_type?: string
}

export interface CreateJokerStopViaSLInput {
  missionId: string
  /**
   * A unified-lead id when the rep picked a known company from the
   * autocomplete; omitted for a free-text walk-in (the SL then records the
   * raw name under manual_entry).
   */
  leadId?: string | null
  /** Display name — always sent; the SL uses it as the manual-entry name. */
  companyName: string
  /**
   * WHY the rep added this joker (operator-mandatory 2026-06-17). Carried on
   * the SL `reason` field → persisted to mission_leads.joker_reason. Replaces
   * the old fixed `ad-hoc` marker; the FE enforces non-empty before submit.
   */
  reason: string
  /**
   * Ort (city) of a free-text walk-in — persisted on the
   * rep_discovered_companies row the SL mints from manual_entry (the table
   * carries `city`). Inherent from the lead for a PICKED company, so only
   * carried for walk-ins. Optional: a walk-in without a known Ort omits it.
   */
  city?: string | null
}

// The joker is an ad-hoc add-stop, NOT a justify form: the rep is never
// prompted for a reason, so the SL `reason` field carries a fixed marker.
export const JOKER_ADHOC_REASON = 'ad-hoc'

/**
 * Create a joker stop against a mission via the Service Layer and return the
 * created mission_lead stop. Fails loud if the SL rejects OR if the success
 * envelope carries no stop id (a stop we can't navigate into is a failure).
 */
export async function createJokerStopViaSL(
  input: CreateJokerStopViaSLInput,
): Promise<JokerStopCreated> {
  return withWrappedError('Failed to create joker stop', async () => {
    const ctx = await buildCtx()
    // SL's JokerCreateRequest (app/models/jokers.py) requires EXACTLY ONE source:
    // { lead_id | discovered_company_id | manual_entry | google_autocomplete }.
    // There is NO top-level `company_name` field — sending one was silently
    // DROPPED (BaseModel ignores extras) → source_count=0 → INVALID_JOKER_SOURCE
    // 400 (raised without logging), which is why EVERY free-text walk-in failed
    // with no error anywhere (SL-diagnosed 2026-06-15). So:
    //  - picked company (autocomplete/existing lead) → lead_id is the source.
    //  - free-text walk-in (no lead_id) → manual_entry { name } is the source;
    //    the SL handler creates the rep_discovered_companies row from it.
    // WHY the joker was added → SL `reason` (persisted to joker_reason). The FE
    // makes it mandatory, but default to the legacy marker if an offline-queued
    // payload predates the field so a replay never 4xx's on an empty reason.
    const body: Record<string, unknown> = { reason: input.reason?.trim() || JOKER_ADHOC_REASON }
    if (input.leadId) {
      body.lead_id = input.leadId
    } else {
      // Walk-in: name is the source; carry Ort as `city` (the
      // rep_discovered_companies column the SL mints the row into).
      const manual: Record<string, unknown> = { name: input.companyName }
      if (input.city && input.city.trim()) manual.city = input.city.trim()
      body.manual_entry = manual
    }

    const { data } = await serviceFetch<unknown>(
      `/v1/missions/${input.missionId}/joker`,
      { method: 'POST', body: JSON.stringify(body), ctx },
    )

    // Envelope is `{ data: { stop: { id, ... } } }`; some SL routes flatten
    // to `{ stop: {...} }` or to the stop directly. Accept all, then demand
    // an id.
    const top = (data ?? {}) as Record<string, unknown>
    const inner = ((top.data ?? top) as Record<string, unknown>) ?? {}
    const stop = ((inner.stop ?? inner) as Record<string, unknown>) ?? {}
    const rawId = stop.id
    const id = typeof rawId === 'string' && rawId ? rawId : null
    if (!id) {
      throw new Error(
        'Service layer returned no stop id for the created joker',
      )
    }
    return { ...(stop as object), id } as JokerStopCreated
  })
}

/**
 * Remove a joker stop from a mission via the Service Layer
 * (DELETE /v1/missions/{mission_id}/jokers/{stop_id}, SL #1521).
 *
 * FAIL-LOUD (D-33): a 404 (unknown stop) or 409 (already visited — a worked
 * stop is not a mistake to undo) propagates as a typed ServiceHttpError wrapped
 * with a domain prefix. There is NO direct-Supabase delete and NO silent
 * swallow — the caller surfaces the error and refetches the board on success.
 */
export async function removeJokerStopViaSL(input: {
  missionId: string
  stopId: string
}): Promise<void> {
  return withWrappedError('Failed to remove joker stop', async () => {
    const ctx = await buildCtx()
    await serviceFetch<unknown>(
      `/v1/missions/${input.missionId}/jokers/${input.stopId}`,
      { method: 'DELETE', ctx },
    )
  })
}
